import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import ProfileForm from "../../../components/profile/ProfileForm/ProfileForm";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import ROUTES from "../../../config/routes";
import getCompleteUserProfile from "../../../features/profile/services/getCompleteUserProfile";
import updateCompleteUserProfile from "../../../features/profile/services/updateCompleteUserProfile";
import { useAuth } from "../../../features/auth/context/AuthContext";
import "./EditProfile.css";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
function normalizeApiProfileToFormValues(apiData = {}) {
  const profile = apiData?.profile || {};
  const preferences = apiData?.preferences || {};
  return {
    name: apiData?.full_name || "",
    email: apiData?.email || "",
    contactNumber: apiData?.mobile || "",
    whatsappNumber: profile?.whatsapp_number || "",
    age: profile?.age !== undefined && profile?.age !== null ? String(profile.age) : "",
    gender: profile?.gender ? String(profile.gender) : "",
    occupation: profile?.occupation || "",
    nationality: profile?.nationality_id !== undefined && profile?.nationality_id !== null ? String(profile.nationality_id) : "",
    city: profile?.city_id !== undefined && profile?.city_id !== null ? String(profile.city_id) : "",
    district: profile?.neighbourhood_id !== undefined && profile?.neighbourhood_id !== null ? String(profile.neighbourhood_id) : "",
    preferredCity: Array.isArray(preferences?.preferred_cities) ? preferences.preferred_cities.map(item => ({
      id: item?.id,
      name: item?.name
    })) : Array.isArray(preferences?.preferred_city_id) ? preferences.preferred_city_id.map(item => String(item)) : [],
    preferredNeighbourhood: Array.isArray(preferences?.preferred_neighbourhoods) ? preferences.preferred_neighbourhoods.map(item => ({
      id: item?.id,
      name: item?.name
    })) : Array.isArray(preferences?.preferred_neighbourhood_id) ? preferences.preferred_neighbourhood_id.map(item => String(item)) : [],
    budgetMin: preferences?.budget_min !== undefined && preferences?.budget_min !== null ? String(preferences.budget_min) : "100",
    budgetMax: preferences?.budget_max !== undefined && preferences?.budget_max !== null ? String(preferences.budget_max) : "50000",
    furnishedPreference: preferences?.furnished_preference || "",
    roomTypePreference: Array.isArray(preferences?.room_type_preference) ? preferences.room_type_preference.map(item => String(item)) : [],
    houseTypePreference: Array.isArray(preferences?.house_type_preference) ? preferences.house_type_preference.map(item => String(item)) : [],
    genderPreference: preferences?.gender_preference || "",
    aboutMe: profile?.about_me || ""
  };
}
function normalizeLayoutUser(sourceUser = {}) {
  return {
    ...sourceUser,
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    contactNumber: sourceUser?.mobile || sourceUser?.contactNumber || "",
    mobile: sourceUser?.mobile || sourceUser?.contactNumber || "",
    whatsappNumber: sourceUser?.profile?.whatsapp_number || sourceUser?.whatsappNumber || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function getEmptyInitialValues(currentUser = {}) {
  return {
    name: currentUser?.full_name || currentUser?.name || "",
    email: currentUser?.email || "",
    contactNumber: currentUser?.mobile || currentUser?.contactNumber || "",
    whatsappNumber: "",
    age: "",
    gender: "",
    occupation: "",
    nationality: "",
    city: "",
    district: "",
    preferredCity: [],
    preferredNeighbourhood: [],
    budgetMin: "100",
    budgetMax: "50000",
    furnishedPreference: "",
    roomTypePreference: [],
    houseTypePreference: [],
    genderPreference: "",
    aboutMe: ""
  };
}
export default function EditProfile() {
  useLanguage();
  useDocumentTitle("Edit Profile");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(null);
  const navigate = useNavigate();
  const {
    user: authUser,
    setUser: setAuthUser
  } = useAuth();
  const layoutUser = useMemo(() => normalizeLayoutUser(authUser || {}), [authUser]);
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const response = await getCompleteUserProfile();
        if (!isMounted) return;
        if (response?.status && response?.data) {
          const apiUser = response.data;
          setAuthUser(prev => ({
            ...(prev || {}),
            user_id: apiUser?.user_id || prev?.user_id || "",
            id: apiUser?.user_id || prev?.id || "",
            name: apiUser?.full_name || prev?.name || "",
            full_name: apiUser?.full_name || prev?.full_name || "",
            email: apiUser?.email || prev?.email || "",
            mobile: apiUser?.mobile || prev?.mobile || "",
            profile_status: apiUser?.profile_status || prev?.profile_status || "",
            nafath_status: apiUser?.nafath_status || prev?.nafath_status || ""
          }));
          setFormInitialValues(normalizeApiProfileToFormValues(apiUser));
          return;
        }
        setFormInitialValues(getEmptyInitialValues(authUser || {}));
      } catch (error) {
        if (isMounted) {
          setFormInitialValues(getEmptyInitialValues(authUser || {}));
        }
        console.error("Get complete user profile error:", error);
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };
    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, [setAuthUser]);
  const handleSubmit = async payload => {
    try {
      setIsSubmitting(true);
      const response = await updateCompleteUserProfile(payload);
      if (response?.status) {
        const refreshedProfileResponse = await getCompleteUserProfile();
        if (refreshedProfileResponse?.status && refreshedProfileResponse?.data) {
          const latestUser = refreshedProfileResponse.data;
          setAuthUser(prev => ({
            ...(prev || {}),
            user_id: latestUser?.user_id || prev?.user_id || "",
            id: latestUser?.user_id || prev?.id || "",
            name: latestUser?.full_name || prev?.name || "",
            full_name: latestUser?.full_name || prev?.full_name || "",
            email: latestUser?.email || prev?.email || "",
            mobile: latestUser?.mobile || prev?.mobile || "",
            profile_status: latestUser?.profile_status || prev?.profile_status || "",
            nafath_status: latestUser?.nafath_status || prev?.nafath_status || ""
          }));
        }
        toast.success(t(response?.message || "Profile updated successfully."));
        navigate(ROUTES.PROFILE);
        return;
      }
      toast.error(t(response?.message || "Unable to update profile."));
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || "Failed to update profile."));
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={layoutUser} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={layoutUser} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={layoutUser} />


        <main className="bb-edit-profile-main">
          <Container fluid>
            <div className="bb-edit-profile__back-wrap">
              <Button variant="link" className="bb-edit-profile__back-btn" onClick={() => navigate(ROUTES.PROFILE)}>

                <HiOutlineArrowLeft className="me-2" />{t("Back to Profile")}

              </Button>
            </div>

            <div className="bb-edit-profile-hero">
              <div>
                <h1 className="bb-edit-profile-hero__title">{t("Edit Profile")}</h1>
                <p className="bb-edit-profile-hero__subtitle">{t("Update your personal information and living preferences.")}

                </p>
              </div>
            </div>

            <Card className="bb-edit-profile-card">
              <Card.Body>
                {isLoadingProfile || !formInitialValues ? <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" role="status" />
                  </div> : <ProfileForm initialValues={formInitialValues} submitLabel={t(isSubmitting ? "Updating..." : "Update Profile")} onSubmit={handleSubmit} onCancel={() => navigate(ROUTES.PROFILE)} lockIdentityFields={true} />}
              </Card.Body>
            </Card>
          </Container>
        </main>
      </div>
    </div>;
}
